'use client'

import React, {useEffect, useState, type ReactNode, useCallback} from 'react'
import {useSearchParams} from 'react-router-dom'
import {
    DocumentTextIcon,
    UserCircleIcon,
    ArrowTopRightOnSquareIcon,
    SparklesIcon,
    ListBulletIcon,
    BriefcaseIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'
import {User} from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {BackendApi} from "../../../../../api/api.ts";
import {BackendAPITypes} from "../../../../../generated/blueprint.ts";
import type {PayrollProvider} from "../../../../../generated/enums.ts";
import {Tab, Tabs} from "../../../../../components/general/Tabs.tsx";
import {RoleEnumToString} from "../../../../../api/accessControl/Role.ts";
import {signOut} from "firebase/auth";
import {auth} from "../../../../../firebaseApp.ts";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {debounce} from "@mui/material";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {AutomationRule} from "../partnerCreateBlacklistRule/types.ts";
import {RulesTab} from "./RulesTab.tsx";
import {DocumentActivityTab} from "./DocumentActivityTab.tsx";
import {TeammatesTab} from "./TeammatesTab.tsx";
import {Client} from "../../Client.ts";
import {DocumentActivityRow} from "./DocumentActivityTab.tsx";
import {SsoAccountLinkingSection} from "../../../../../components/auth/SsoAccountLinkingSection.tsx";
import {
    AvailablePayrollProvider,
    canAddPayrollClients,
    getPayrollConnectionStatus,
    listAvailablePayrollProviders,
    PayrollConnectionStatus,
    reconnectPayrollProvider,
    startPayrollConnection,
} from "../../../../../lib/payrollConnect.ts";
import {nangoConnectUiOpener} from "../../../../../lib/nangoConnectUI.ts";
import {PayrollTab} from "./PayrollTab.tsx";

function ActiveConnectionRow({name, status}: { name: string, status: string }) {
    return (
        <div className="border rounded-lg p-4 flex justify-between items-center">
            <div className="flex gap-6 items-center">
                <span className="relative flex size-3">
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-300 opacity-75"></span>  <span
                    className="relative inline-flex size-3 rounded-full bg-green-400"></span></span>
                <div>
                    <h4 className="font-medium">{name}</h4>
                    <p className="text-sm text-gray-600">{status}</p>
                </div>
            </div>
        </div>
    )
}

function SettingRow({name, children, full}: { name: string, children: ReactNode, full?: boolean }) {
    return (
        <div className={("justify-between bg-gray-50 p-3 rounded-md" + (!full ? ' sm:flex' : ''))}>
            <dt className="font-medium text-gray-900 sm:w-64  sm:pr-6">
                {name}
            </dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0">
                {children}
            </dd>
        </div>
    )
}

interface BusinessSettings {
    settings: {
        IGNORE_ITEMS_FROM_VAT_CALCULATION_PROMPT: string
    }
}


export default function PartnerSettingsPage({currentUser}: { currentUser: User }) {
    const [searchParams, setSearchParams] = useSearchParams()
    const currentTab = searchParams.get('tab') ?? 'basis'
    const setTab = (slug: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.set('tab', slug)
            return next
        }, {replace: true})
    }

    const mappingId = searchParams.get('mapping_id') ?? undefined
    const action = searchParams.get('action') ?? undefined
    const administrationId = searchParams.get('administration_id') ?? undefined
    const returnTo = searchParams.get('return_to') ?? undefined
    const highlightedRuleId = searchParams.get('rule_id') ?? undefined

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [integrations, setIntegrations] = useState<BackendAPITypes.BACKENDPreHandleSyncApiV1BusinessMyConnectionsGetResponseBody>([])
    const [vatIgnoreText, setVatIgnoreText] = useState<string>('')
    const [isRefining, setIsRefining] = useState<boolean>(false)
    const [rules, setRules] = useState<AutomationRule[]>([])
    const [clients, setClients] = useState<Client[]>([])
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const [documents, setDocuments] = useState<DocumentActivityRow[]>([])
    const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false)
    const [payrollConnection, setPayrollConnection] = useState<PayrollConnectionStatus | null>(null)
    const [payrollConnectionError, setPayrollConnectionError] = useState<boolean>(false)
    const [connectingPayrollProvider, setConnectingPayrollProvider] = useState<PayrollProvider | null>(null)
    const [isPayrollReconnecting, setIsPayrollReconnecting] = useState<boolean>(false)
    const [payrollReconnectError, setPayrollReconnectError] = useState<string | null>(null)
    const [availablePayrollProviders, setAvailablePayrollProviders] = useState<AvailablePayrollProvider[]>([])
    const api = new BackendApi(currentUser.getToken())
    const canSeePayroll = currentUser.flags.getFlag('PARTNER__CAN_SEE_PAYROLL')

    const handleDeleteRule = async (id: string) => {
        const response = await api.Partner__deleteAutomationRule({id: id})
        setRules(response as AutomationRule[])
    }

    const debouncedUpdateVatIgnoreText = useCallback(
        debounce(async (text: string) => {
            await api.Me__business__updateSetting({
                setting_name: "IGNORE_ITEMS_FROM_VAT_CALCULATION_PROMPT",
                setting_value: text,
            });
        }, 1000),
        []
    );

    const handleVatIgnoreTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setVatIgnoreText(newText);
        debouncedUpdateVatIgnoreText(newText);
    };


    async function onLoad() {
        const integrations = await api.Business__myConnections({})
        setIntegrations(integrations)
        const settings = await api.Me__business__settings({}) as BusinessSettings
        setVatIgnoreText(settings.settings.IGNORE_ITEMS_FROM_VAT_CALCULATION_PROMPT)
        const rulesResponse = await api.Partner__getAutomationRules({})
        const rulesData = rulesResponse.map(m => {
            return {rule_id: m.rule_id, business_name: m.business_name, rule: m.rule}
        })
        setRules(rulesData as AutomationRule[])

        const clientsResponse = await api.Partner__myClients(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) + 1)
        setClients(clientsResponse.businesses as Client[])

        if (canSeePayroll) {
            try {
                const [status, providers] = await Promise.all([
                    getPayrollConnectionStatus(api),
                    listAvailablePayrollProviders(api),
                ])
                setPayrollConnection(status)
                setAvailablePayrollProviders(providers)
                setPayrollConnectionError(false)
            } catch (error) {
                console.error("Error loading payroll connection status:", error)
                setPayrollConnectionError(true)
            }
        }

        setIsLoading(false)
    }

    async function handleConnectPayrollProvider(provider: PayrollProvider) {
        setConnectingPayrollProvider(provider)
        try {
            const status = await startPayrollConnection(api, nangoConnectUiOpener, provider)
            setPayrollConnection(status)
            setPayrollConnectionError(false)
        } catch (error) {
            console.error("Error connecting to payroll provider:", error)
            alert("Er is een fout opgetreden bij het koppelen van het salarispakket. Probeer het later opnieuw.")
        } finally {
            setConnectingPayrollProvider(null)
        }
    }

    async function handleReconnectPayrollProvider() {
        setIsPayrollReconnecting(true)
        setPayrollReconnectError(null)
        try {
            const status = await reconnectPayrollProvider(api, nangoConnectUiOpener)
            setPayrollConnection(status)
            setPayrollConnectionError(false)
        } catch (error) {
            console.error("Error reconnecting to payroll provider:", error)
            setPayrollReconnectError("Er is een fout opgetreden bij het opnieuw koppelen van het salarispakket. Probeer het later opnieuw.")
        } finally {
            setIsPayrollReconnecting(false)
        }
    }

    async function loadDocumentsForClient(client: Client | null) {
        if (!client) {
            setDocuments([])
            return
        }

        try {
            setIsLoadingDocuments(true)
            const response = await api.Partner__documentActivityDocuments(client.id)
            setDocuments(response.documents as DocumentActivityRow[])
        } catch (error) {
            console.error("Error loading documents:", error)
            setDocuments([])
            throw error
        } finally {
            setIsLoadingDocuments(false)
        }
    }

    const handleClientSelect = async (client: Client | null) => {
        setSelectedClient(client)
        try {
            await loadDocumentsForClient(client)
        } catch (error) {
            alert("Er is een fout opgetreden bij het laden van documenten. Probeer het later opnieuw.")
            throw error
        }
    }

    async function redirectToChiftForConnection() {
        try {
            const response = await api.Partner__connectFinancialService({
                app_hostname: window.location.hostname,
            });
            window.location.replace(response.url)
        } catch (error) {
            console.error("Error connecting to Chift:", error);
        }
    }

    async function refineText() {
        const prompt = await api.Partner__refineVatIgnorePrompt({
            partner_prompt: vatIgnoreText
        })
        setVatIgnoreText(prompt.refined_prompt);
    }

    async function handleRefineClick() {
        setIsRefining(true)
        try {
            await refineText()
        } catch (error) {
            alert("Er is een fout opgetreden bij het verfijnen van de prompt. Probeer het later opnieuw.");
        } finally {
            setIsRefining(false)
        }
    }

    const connectedPayrollProvider: AvailablePayrollProvider | null =
        payrollConnection?.isConnected && payrollConnection.provider && payrollConnection.name
            ? {provider: payrollConnection.provider, name: payrollConnection.name}
            : null
    const payrollProviderCards = connectedPayrollProvider ? [connectedPayrollProvider] : availablePayrollProviders
    const connectedPayrollProviderName = connectedPayrollProvider?.name ?? null

    useEffect(() => {
        onLoad()
    }, []);

    return (
        <>
            <Shell currentUser={currentUser} isLoading={isLoading}>
                <h1 className="weight-600 text-3xl">Instellingen</h1>

                <div className="mt-12 max-w-5xl">
                    <Tabs activeSlug={currentTab} onTabChange={setTab}>
                        <Tab label={'Basis'} slug="basis" icon={UserCircleIcon}>
                            <div>
                                <dl className="space-y-3 text-sm leading-6">
                                    <h3 className="text-xl font-gray-900 py-3 pt-6">Gegevens over jouw account</h3>
                                    <SettingRow name="Volledige naam">
                                        <div className="text-gray-900">{currentUser.getFullName()}</div>
                                    </SettingRow>
                                    <SettingRow name="E-mailadres">
                                        <div className="text-gray-900">{currentUser.email}</div>
                                    </SettingRow>
                                    <SettingRow name="Accounttype">
                                        <div className="text-gray-900">{RoleEnumToString(currentUser.role)}</div>
                                    </SettingRow>

                                </dl>

                                <SsoAccountLinkingSection/>

                                <button className="text-base bg-primary text-white px-4 py-2 rounded-md mt-12"
                                        onClick={async () => {
                                            await signOut(auth).then(() => {
                                                window.location.replace('/')
                                            })

                                        }}>Uitloggen
                                </button>
                            </div>
                        </Tab>
                        <Tab label={'Teamleden'} slug="teammates" icon={UsersIcon}>
                            <TeammatesTab currentUser={currentUser}/>
                        </Tab>
                        <Tab label={'Integraties'} slug="integrations" icon={ArrowTopRightOnSquareIcon}>
                            <div>
                                <h3 className="text-xl font-gray-900 py-3 pt-6">Koppelingen met externe databronnen</h3>
                                <div className="space-y-4 mt-4">
                                    <div className="border rounded-lg p-4 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-medium">Boekhoudpakket</h4>
                                            <p className="text-sm text-gray-600">Koppel met je boekhoudsoftware</p>
                                        </div>
                                        <PrimaryButton
                                            onClick={() => {
                                                redirectToChiftForConnection()
                                            }}
                                            title="Integratie toevoegen"/>
                                    </div>
                                    {canSeePayroll && payrollProviderCards.map(provider => {
                                        const isConnectedProvider = payrollConnection?.isConnected && payrollConnection.provider === provider.provider
                                        const showNmbrsOnboarding = provider.provider === 'nmbrs' && !isConnectedProvider
                                        return (
                                            <div key={provider.provider}
                                                 className="border rounded-lg p-4">
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <h4 className="font-medium">{provider.name}</h4>
                                                        <p className="text-sm text-gray-600">Koppel met je {provider.name} salarisadministratie</p>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2">
                                                        {isConnectedProvider
                                                            ? (
                                                                <>
                                                                    <span className="text-sm font-medium text-green-600">Gekoppeld</span>
                                                                    {canAddPayrollClients(provider.provider) && (
                                                                        <SecondaryButton
                                                                            onClick={handleReconnectPayrollProvider}
                                                                            disabled={isPayrollReconnecting}
                                                                            loading={isPayrollReconnecting}
                                                                            title="Klanten toevoegen / opnieuw koppelen"/>
                                                                    )}
                                                                </>
                                                            )
                                                            : payrollConnectionError
                                                                ? <span className="text-sm font-medium text-red-600">Status onbekend</span>
                                                                : <PrimaryButton
                                                                    onClick={() => handleConnectPayrollProvider(provider.provider)}
                                                                    disabled={connectingPayrollProvider !== null}
                                                                    loading={connectingPayrollProvider === provider.provider}
                                                                    title={connectingPayrollProvider === provider.provider
                                                                        ? `${provider.name} koppelen…`
                                                                        : `${provider.name} koppelen`}/>}
                                                    </div>
                                                </div>
                                                {isConnectedProvider && payrollReconnectError && (
                                                    <div className="mt-3 p-3 border border-red-200 rounded-md bg-red-50 text-sm text-red-700">
                                                        {payrollReconnectError}
                                                    </div>
                                                )}
                                                {showNmbrsOnboarding && (
                                                    <div className="mt-3 p-3 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-700">
                                                        <p>
                                                            Maak in Nmbrs met één e-mailadres een klantlogin aan op elke debiteur die je in
                                                            PocketCFO wilt (Nmbrs vraagt hierbij om tweestapsverificatie). Koppel daarna met
                                                            die login.
                                                        </p>
                                                        <p className="mt-2">
                                                            Exporteer de journaalposten van deze klanten niet ook nog vanuit Nmbrs naar een
                                                            ander boekhoudpakket, anders worden ze dubbel geboekt.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {canSeePayroll && payrollConnectionError && (
                                        <div className="border rounded-lg p-4 flex justify-between items-center">
                                            <div>
                                                <h4 className="font-medium">Salarisadministratie</h4>
                                                <p className="text-sm text-gray-600">Koppel met je salarispakket</p>
                                            </div>
                                            <span className="text-sm font-medium text-red-600">Status onbekend</span>
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-gray-900 py-3 pt-12">Actieve koppelingen</h3>
                                <div className="space-y-4 mt-4">
                                    {integrations
                                        ?.filter((integration) => integration.status === 'CONNECTED')
                                        .map(integration => <ActiveConnectionRow key={integration.id}
                                                                                 name={integration.name}
                                                                                 status={integration.status}/>)}
                                    {canSeePayroll && payrollConnection?.isConnected &&
                                        <ActiveConnectionRow key="payroll"
                                                             name={payrollConnection.name ?? ''}
                                                             status={payrollConnection.status ?? 'CONNECTED'}/>}
                                </div>
                            </div>
                        </Tab>
                        <Tab label={'BTW'} slug="vat" icon={DocumentTextIcon}>
                            <div>
                                <div className="space-y-6">
                                    <div className="p-6 rounded-md">
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                                            Automatisch uitsluiten van BTW berekening
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Beschrijf in je eigen woorden welke documenten je automatisch wilt uitsluiten van BTW berekeningen (voorbeeld: eten & drinken in Horeca, relatiegeschenken etc)
                                        </p>
                                        <div className="relative">
                                            <textarea
                                                className="w-full min-h-[8rem] p-3 pb-16 border rounded-md"
                                                placeholder="Bijvoorbeeld: Eten, kleding, ..."
                                                value={vatIgnoreText}
                                                onChange={handleVatIgnoreTextChange}
                                            />
                                            <div className="absolute bottom-4 right-4">
                                                <SecondaryButton
                                                    title="Verbeter prompt"
                                                    icon={SparklesIcon}
                                                    loading={isRefining}
                                                    onClick={handleRefineClick}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Tab>
                        <Tab label={'Regels'} slug="rules" icon={ListBulletIcon}>
                            <RulesTab rules={rules} onDeleteRule={handleDeleteRule} highlightedRuleId={highlightedRuleId} />
                        </Tab>
                        <Tab label={'Document activiteit'} slug="document-activity" icon={DocumentTextIcon}>
                            <DocumentActivityTab
                                clients={clients}
                                selectedClient={selectedClient}
                                onClientSelect={handleClientSelect}
                                documents={documents}
                                isLoadingDocuments={isLoadingDocuments}
                            />
                        </Tab>
                        {canSeePayroll && (
                            <Tab label={'Loonadministratie'} slug="payroll" icon={BriefcaseIcon}>
                                <PayrollTab
                                    api={api}
                                    user={currentUser}
                                    clients={clients}
                                    mappingId={mappingId}
                                    action={action}
                                    administrationId={administrationId}
                                    returnTo={returnTo}
                                    providerName={connectedPayrollProviderName}
                                />
                            </Tab>
                        )}

                    </Tabs>
                </div>

            </Shell>


        </>
    )
}
